import api from "./axios";

const CRITERIA_URL = "/criteria";

// ----- Criterion -----
export const getCriterias = () => api.get(CRITERIA_URL);
export const createCriterion = (criterion) => api.post(CRITERIA_URL, criterion);
export const updateCriterion = (id, data) => api.put(`${CRITERIA_URL}/${id}`, data);
export const getCriterion = (id) => api.get(`${CRITERIA_URL}/${id}`);
export const deleteCriterion = (id) => api.delete(`${CRITERIA_URL}/${id}`);

// ----- UserCriterion -----
export const getUserCriterias = (userId, sessionId) =>
  api.get(`${CRITERIA_URL}/user/${userId}/session/${sessionId}`);

/**
 * Unified update for a UserCriterion
 */
export const updateUserCriterion = (criterionId, userId, sessionId, action, value = null, amount = 1) => {
  const url = `${CRITERIA_URL}/${criterionId}/${userId}/session/${sessionId}`;
  const params = { action, amount };

  if (action === "set_boolean" || action === "set_text") {
    return api.put(url, { value }, { params });
  } else if (action === "set_text_and_count") {
    return api.put(url, value, { params });
  } else {
    return api.put(url, {}, { params });
  }
};

// ----- Convenience wrappers -----
export const incrementUserCriterion = (criterionId, userId, sessionId) =>
  updateUserCriterion(criterionId, userId, sessionId, "increment");

export const decrementUserCriterion = (criterionId, userId, sessionId) =>
  updateUserCriterion(criterionId, userId, sessionId, "decrement");

export const setBooleanValue = (criterionId, userId, sessionId, value) =>
  updateUserCriterion(criterionId, userId, sessionId, "set_boolean", value);

export const setTextValue = (criterionId, userId, sessionId, value) =>
  updateUserCriterion(criterionId, userId, sessionId, "set_text", value);

export const setCountValue = (criterionId, userId, sessionId, value) =>
  updateUserCriterion(criterionId, userId, sessionId, "set_count", value);

export const setTextAndCountValue = (criterionId, userId, sessionId, text = undefined, count = undefined) => {
  const payload = {};
  if (text !== undefined) payload.text = text;
  if (count !== undefined) payload.count = count;

  return updateUserCriterion(
    criterionId,
    userId,
    sessionId,
    "set_text_and_count",
    payload
  );
};

// ----- Publish / Undo Publish -----
export const publishUserCriterion = (criterionId, userId, sessionId) =>
  updateUserCriterion(criterionId, userId, sessionId, "publish");

export const undoPublishUserCriterion = (criterionId, userId, sessionId) =>
  updateUserCriterion(criterionId, userId, sessionId, "undo_publish");

/**
 * Get all user criteria entries for a specific criterion (optionally filtered by session)
 * @param {number} criterionId 
 * @param {number|null} sessionId 
 * @returns {Promise<AxiosResponse>}
 */
export const getUserCriteriasForCriterion = (criterionId, sessionId = null) => {
  const params = sessionId ? { sessionId } : {};
  return api.get(`${CRITERIA_URL}/${criterionId}/users`, { params });
};

export const setPublishState = (historyId, publish) =>
  api.put(`${CRITERIA_URL}/history/${historyId}/publish`, null, {
    params: { publish }
  });

// ----- User History -----
export const getUserHistory = (sessionId, teamId) =>
  api.get(`${CRITERIA_URL}/history/session/${sessionId}`, {
    params: { team_id: teamId }
  });

/**
 * Delete a specific TeamCriterion history entry
 * @param {number} historyId
 */
export const deleteUserHistoryEntry = (historyId) =>
  api.delete(`${CRITERIA_URL}/history/${historyId}`);

/**
 * Swap a user criterion history entry to a team criterion history entry
 * @param {number} historyId
 * @param {number|null} teamId - The specific team to swap to (optional, defaults to user's current team)
 * @param {number|null} changeValue - The change value to apply to the team's current value
 */
export const swapUserHistoryToTeam = (historyId, teamId = null, changeValue = null) => {
  const params = {};
  if (teamId !== null) params.team_id = teamId;
  if (changeValue !== null) params.change_value = changeValue;
  return api.post(`${CRITERIA_URL}/history/${historyId}/swap-to-team`, null, { params });
};

/**
 * Swap a user criterion history entry to another user
 * @param {number} historyId
 * @param {number} targetUserId - The user to swap to
 * @param {number|null} changeValue - The change value to apply to the target user's current value
 */
export const swapUserHistoryToUser = (historyId, targetUserId, changeValue = null) => {
  const params = changeValue !== null ? { change_value: changeValue } : {};
  return api.post(`${CRITERIA_URL}/history/${historyId}/swap-to-user/${targetUserId}`, null, { params });
};

/**
 * Update a user criterion history entry
 * @param {number} historyId
 * @param {object} data - { int_value, bool_value, text_value }
 */
export const updateUserHistoryEntry = (historyId, data) =>
  api.put(`${CRITERIA_URL}/history/${historyId}`, data);



